import type { Patient } from "@elation/api-client/src/stitch-web/models";
import { SearchPatientsSortBy } from "@elation/api-client/src/stitch-web/models/searchPatientsSortBy";
import { HeaderText } from "@src/components/general/HeaderText";
import { Text } from "@src/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { Button } from "@src/components/ui/button";
import { DataTable } from "@src/components/ui/data-table";
import { Skeleton } from "@src/components/ui/skeleton";
import { type ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Eye,
} from "lucide-react";
import { useMemo } from "react";

export type PatientSortField = SearchPatientsSortBy;
export type PatientSortOrder = "asc" | "desc";

export interface PatientSearchProps {
  patients: Patient[];
  isLoading?: boolean;
  /** True when the search request failed (network / 5xx / non-recoverable 4xx). */
  isError?: boolean;
  onPatientSelect: (patientId: number) => void;

  /** Active server sort field. Omit for a non-interactive table. */
  sortBy?: PatientSortField;
  /** Active server sort order. */
  order?: PatientSortOrder;
  /** Called when the user clicks a server-sortable column header. Omit to hide sort affordances. */
  onSortChange?: (sortBy: PatientSortField, order: PatientSortOrder) => void;

  /** True when the server returned a nextCursor for the current page. */
  hasNextPage?: boolean;
  /** True when there is a previous page on the client's cursor stack. */
  hasPrevPage?: boolean;
  onNextPage?: () => void;
  onPrevPage?: () => void;
}

type PatientRow = Patient & { fullName: string };

function nextOrder(
  current: { sortBy: PatientSortField; order: PatientSortOrder },
  clicked: PatientSortField,
): PatientSortOrder {
  if (current.sortBy !== clicked) return "asc";
  return current.order === "asc" ? "desc" : "asc";
}

function SortIcon({ state }: { state: "asc" | "desc" | false }) {
  if (state === "asc") return <ArrowUp className="ml-1 size-3.5" />;
  if (state === "desc") return <ArrowDown className="ml-1 size-3.5" />;
  return <ArrowUpDown className="ml-1 size-3.5" />;
}

export const PatientSearch = ({
  patients,
  isLoading = false,
  isError = false,
  onPatientSelect,
  sortBy,
  order,
  onSortChange,
  hasNextPage = false,
  hasPrevPage = false,
  onNextPage,
  onPrevPage,
}: PatientSearchProps) => {
  const serverSortEnabled = Boolean(onSortChange && sortBy && order);
  const rows = useMemo<PatientRow[]>(
    () =>
      patients.map((patient) => ({
        ...patient,
        fullName: `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim(),
      })),
    [patients],
  );

  const columns = useMemo<ColumnDef<PatientRow>[]>(() => {
    const serverSortHeader =
      (
        label: string,
        field: PatientSortField,
      ): ColumnDef<PatientRow>["header"] =>
      () => {
        if (!serverSortEnabled || !onSortChange || !sortBy || !order) {
          return label;
        }
        const state = sortBy === field ? order : false;
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 text-muted-foreground"
            onClick={() =>
              onSortChange(field, nextOrder({ sortBy, order }, field))
            }
          >
            {label}
            <SortIcon state={state} />
          </Button>
        );
      };

    return [
      {
        accessorKey: "fullName",
        enableSorting: false,
        header: serverSortHeader("Name", "lastName"),
        cell: ({ row }) => (
          <Text fontWeight={500}>{row.original.fullName || "—"}</Text>
        ),
      },
      {
        accessorKey: "dob",
        enableSorting: false,
        header: "Date of Birth",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.dob || "—"}
          </Text>
        ),
      },
      {
        accessorKey: "sex",
        enableSorting: false,
        header: "Gender",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.sex ?? "—"}
          </Text>
        ),
      },
      {
        id: "address",
        enableSorting: false,
        header: "Address",
        accessorFn: (patient) =>
          [patient.addressLine1, patient.addressLine2]
            .filter(Boolean)
            .join(", "),
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {[row.original.addressLine1, row.original.addressLine2]
              .filter(Boolean)
              .join(", ") || "—"}
          </Text>
        ),
      },
      {
        accessorKey: "city",
        enableSorting: false,
        header: "City",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.city ?? "—"}
          </Text>
        ),
      },
      {
        accessorKey: "state",
        enableSorting: false,
        header: "State",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.state ?? "—"}
          </Text>
        ),
      },
      {
        accessorKey: "zip",
        enableSorting: false,
        header: "Zip Code",
        cell: ({ row }) => (
          <Text sizing="sm" appearance="subdued">
            {row.original.zip ?? "—"}
          </Text>
        ),
      },
      {
        id: "actions",
        enableHiding: false,
        enableSorting: false,
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              size="sm"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={(event) => {
                event.stopPropagation();
                onPatientSelect(row.original.id);
              }}
            >
              <Eye className="mr-1 size-3.5" />
              View
            </Button>
          </div>
        ),
      },
    ];
  }, [onPatientSelect, onSortChange, sortBy, order, serverSortEnabled]);

  if (isLoading) {
    return (
      <VerticalFlexbox
        gap="sm"
        className="rounded-xl border border-border bg-card p-6 shadow-soft"
      >
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </VerticalFlexbox>
    );
  }

  if (isError) {
    return (
      <VerticalFlexbox
        alignItems="center"
        justifyContent="center"
        gap="xs"
        className="rounded-xl border border-destructive/50 bg-destructive/5 p-10 text-center"
      >
        <AlertCircle className="size-6 text-destructive" />
        <HeaderText tag="h3" fontWeight={600} className="text-base">
          Unable to load patients
        </HeaderText>
        <Text sizing="sm" appearance="subdued" className="max-w-md">
          Something went wrong retrieving your roster. Adjust your search or try
          again in a moment.
        </Text>
      </VerticalFlexbox>
    );
  }

  if (patients.length === 0) {
    return (
      <VerticalFlexbox
        alignItems="center"
        justifyContent="center"
        gap="xs"
        className="rounded-xl border border-dashed border-border bg-muted/40 p-10 text-center"
      >
        <HeaderText tag="h3" fontWeight={600} className="text-base">
          No patients found
        </HeaderText>
        <Text sizing="sm" appearance="subdued" className="max-w-md">
          Adjust your search filters above to find a matching patient record.
        </Text>
      </VerticalFlexbox>
    );
  }

  return (
    <VerticalFlexbox gap="sm">
      <VerticalFlexbox gap="xs">
        <HeaderText tag="h2" fontWeight={600} className="text-xl">
          Select a patient
        </HeaderText>
        <Text sizing="sm" appearance="subdued">
          {patients.length} result{patients.length === 1 ? "" : "s"} on this
          page — use the search above or column headers to narrow down, then
          click "View".
        </Text>
      </VerticalFlexbox>
      <DataTable<PatientRow, unknown>
        columns={columns}
        data={rows}
        enablePagination={false}
        onRowClick={(patient) => onPatientSelect(patient.id)}
      />
      {(hasPrevPage || hasNextPage) && onNextPage && onPrevPage && (
        <HorizontalFlexbox
          alignItems="center"
          justifyContent="flex-end"
          gap="xs"
          className="py-1"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={onPrevPage}
            disabled={!hasPrevPage}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onNextPage}
            disabled={!hasNextPage}
          >
            Next
          </Button>
        </HorizontalFlexbox>
      )}
    </VerticalFlexbox>
  );
};
